import type { ClientToServer, ServerToClient, OwuiFolder, OwuiChatSummary } from "@lupp/shared";
import { getSocket } from "./socket";

/** Lädt die OWUI-Ordner ("Projekte"), flach (Client baut den Baum). */
export async function listFolders(): Promise<OwuiFolder[]> {
  const socket = getSocket();
  return new Promise<OwuiFolder[]>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "folder_list_result") {
        cleanup();
        resolve(event.folders);
      } else if (event.type === "auth_required") {
        cleanup();
        reject(new Error("auth required"));
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };
    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("folder_list", { type: "folder_list" } satisfies ClientToServer);
  });
}

/** Lädt die (slim) Chats eines Ordners (lazy beim Aufklappen). */
export async function getFolderChats(folderId: string): Promise<OwuiChatSummary[]> {
  const socket = getSocket();
  return new Promise<OwuiChatSummary[]>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "folder_chats_result" && event.folderId === folderId) {
        cleanup();
        resolve(event.chats);
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };
    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("folder_chats", { type: "folder_chats", folderId } satisfies ClientToServer);
  });
}

/** Hängt einen Ordner um (parentId=null ⇒ oberste Ebene). Resolved auf den Ack. */
export async function moveFolder(folderId: string, parentId: string | null): Promise<void> {
  const socket = getSocket();
  return new Promise<void>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "folder_moved" && event.folderId === folderId) {
        cleanup();
        resolve();
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };
    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("folder_move", { type: "folder_move", folderId, parentId } satisfies ClientToServer);
  });
}

/** Legt ein Projekt (Ordner) an und liefert den neuen Ordner zurück. */
export async function createFolder(name: string, description?: string, parentId?: string | null): Promise<OwuiFolder> {
  const socket = getSocket();
  return new Promise<OwuiFolder>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "folder_created") {
        cleanup();
        resolve(event.folder);
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };
    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("folder_create", { type: "folder_create", name, description, parentId } satisfies ClientToServer);
  });
}
